<script lang="ts">
import { defineComponent, reactive, onMounted, onBeforeUnmount, watch } from '@vue/composition-api';
import { nextTick } from '@/_core/utils/nextTick';
import {
    AUTO_SAVE_HOST_KINDS, AutoSaveHostKind, autoSaveHostLabels, readAutoSavePreference, writeAutoSavePreference,
} from '@/pages/components/editSession/autoSavePreference';

/** Browser-local editor preferences. Every change is written immediately; there is no Save button. */
export default defineComponent({
    name: 'EditorPreferences',
    props: {
        /** True while this tab is the visible one; the stored values are re-read on every activation. */
        active: { type: Boolean, default: true },
    },
    setup(props) {
        const autoSave = reactive<Record<AutoSaveHostKind, boolean>>({ 'card-description': false, 'release-goal': false });
        const hosts = AUTO_SAVE_HOST_KINDS.map(kind => ({ kind, label: autoSaveHostLabels[kind] }));
        const refresh = () => { AUTO_SAVE_HOST_KINDS.forEach(kind => { autoSave[kind] = readAutoSavePreference(kind); }); };
        const onChange = async (kind: AutoSaveHostKind, value: boolean) => {
            // Reflect the click first, then show what storage actually holds, so a rejected write is visibly reverted.
            autoSave[kind] = !!value;
            writeAutoSavePreference(kind, !!value);
            await nextTick();
            autoSave[kind] = readAutoSavePreference(kind);
        };
        const onStorage = (event: StorageEvent) => { if (event.key === null || event.key.startsWith('_storiesonboard_autoSaveOnClose_')) refresh(); };
        watch(() => props.active, active => { if (active) refresh(); }, { immediate: true });
        onMounted(() => { refresh(); window.addEventListener('storage', onStorage); });
        onBeforeUnmount(() => window.removeEventListener('storage', onStorage));
        return { autoSave, hosts, onChange, refresh };
    },
});
</script>

<template>
  <v-card flat>
    <v-card-text>
      <div class="text-subtitle-1 font-weight-medium mb-1">
        Editor preferences
      </div>
      <div class="secondaryText--text body-2 mb-3">
        These settings are stored in this browser only. They do not follow you to other browsers or other workspaces.
      </div>
      <v-checkbox
        v-for="host in hosts"
        :key="host.kind"
        :input-value="autoSave[host.kind]"
        :label="host.label"
        hide-details
        dense
        class="mt-1"
        :data-testid="`auto-save-${host.kind}`"
        @change="onChange(host.kind, $event)"
      />
    </v-card-text>
  </v-card>
</template>
